import { Box, Flex, Text } from '@chakra-ui/react';
import React from 'react';
import Select, { StylesConfig, GroupBase, SingleValue, components, DropdownIndicatorProps } from 'react-select';
import { IoChevronDown } from 'react-icons/io5';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

interface StyledSelectProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isSearchable?: boolean;
  size?: 'sm' | 'md' | 'lg';
  menuPlacement?: 'auto' | 'bottom' | 'top';
  label?: string;
}

const DropdownIndicator = (props: DropdownIndicatorProps<SelectOption, false, GroupBase<SelectOption>>) => {
  return (
    <components.DropdownIndicator {...props}>
      <IoChevronDown size={16} color="#64748b" />
    </components.DropdownIndicator>
  );
};

export const StyledSelect: React.FC<StyledSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  isDisabled = false,
  isSearchable = false,
  size = 'lg',
  menuPlacement = 'auto',
  label,
}) => {
  const selectedValue = value
    ? options.find((option) => option.value === value) || null
    : null;

  const heights = {
    sm: '36px',
    md: '40px',
    lg: '48px',
  };

  const fontSizes = {
    sm: '13px',
    md: '14px',
    lg: '15px',
  };

  const customStyles: StylesConfig<SelectOption, false> = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'white' : '#f8fafc',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: state.isFocused ? '#00477D' : '#e2e8f0',
      borderRadius: '14px',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(0,71,125,0.1)' : 'none',
      minHeight: heights[size],
      height: heights[size],
      fontSize: fontSizes[size],
      fontWeight: 400,
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: state.isFocused ? '#00477D' : '#cbd5e1',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 12px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#1a1a2e',
      margin: 0,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#94a3b8',
      margin: 0,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: '0 12px',
      color: state.isFocused ? '#00477D' : '#64748b',
      transition: 'all 0.2s',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderRadius: '14px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflow: 'hidden',
      marginTop: '4px',
      zIndex: 100,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '6px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'rgba(0,71,125,0.1)'
        : state.isFocused
          ? '#f8fafc'
          : 'white',
      color: state.isSelected ? '#00477D' : '#1a1a2e',
      padding: '10px 14px',
      borderRadius: '10px',
      cursor: 'pointer',
      fontWeight: state.isSelected ? 600 : 400,
      fontSize: fontSizes[size],
      transition: 'all 0.15s',
      '&:active': {
        backgroundColor: 'rgba(0,71,125,0.15)',
      },
    }),
    input: (provided) => ({
      ...provided,
      color: '#1a1a2e',
      margin: 0,
      padding: 0,
    }),
  };

  const handleChange = (selected: SingleValue<SelectOption>) => {
    if (selected) {
      onChange(selected.value);
    }
  };

  return (
    <Box width="100%">
      {label && (
        <Text
          fontSize="13px"
          fontWeight={600}
          color="#475569"
          mb={2}
          textTransform="uppercase"
          letterSpacing="0.05em"
        >
          {label}
        </Text>
      )}
      <Select
        options={options}
        value={selectedValue}
        onChange={handleChange}
        styles={customStyles}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        menuPlacement={menuPlacement}
        components={{ DropdownIndicator }}
      />
    </Box>
  );
};

// Menu-style styled select for topbar/navigation
interface StyledMenuSelectProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  icon?: React.ReactNode;
  maxWidth?: string;
  // Optional pinned content rendered below the options (e.g. a "Create…" action).
  footer?: React.ReactNode;
}

export const StyledMenuSelect: React.FC<StyledMenuSelectProps> = ({
  options,
  value,
  onChange,
  icon,
  maxWidth = '150px',
  footer,
}) => {
  const selectedValue = value
    ? options.find((option) => option.value === value) || null
    : null;

  const customStyles: StylesConfig<SelectOption, false> = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'rgba(255,255,255,0.7)' : 'transparent',
      borderWidth: '0',
      borderRadius: '10px',
      boxShadow: 'none',
      minHeight: '36px',
      height: '36px',
      fontSize: '13px',
      fontWeight: 400,
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.5)',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 8px',
      maxWidth: maxWidth,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#1a365d',
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#64748b',
      margin: 0,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      padding: '0 8px 0 0',
      color: '#1a365d',
      transition: 'all 0.2s',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderRadius: '14px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflow: 'hidden',
      marginTop: '4px',
      minWidth: '180px',
      zIndex: 100,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '6px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'rgba(0,71,125,0.1)'
        : state.isFocused
          ? '#f8fafc'
          : 'white',
      color: state.isSelected ? '#00477D' : '#1a1a2e',
      padding: '10px 14px',
      borderRadius: '10px',
      cursor: 'pointer',
      fontWeight: state.isSelected ? 600 : 400,
      fontSize: '14px',
      transition: 'all 0.15s',
      '&:active': {
        backgroundColor: 'rgba(0,71,125,0.15)',
      },
    }),
    input: (provided) => ({
      ...provided,
      color: '#1a1a2e',
      margin: 0,
      padding: 0,
    }),
  };

  const handleChange = (selected: SingleValue<SelectOption>) => {
    if (selected) {
      onChange(selected.value);
    }
  };

  // Custom control with icon
  const CustomControl = ({ children, ...props }: any) => {
    return (
      <components.Control {...props}>
        {icon && (
          <Flex pl={2} align="center">
            {icon}
          </Flex>
        )}
        {children}
      </components.Control>
    );
  };

  // Pin a footer (e.g. a "Create organization" action) below the options.
  const CustomMenuList = ({ children, ...props }: any) => (
    <components.MenuList {...props}>
      {children}
      {footer}
    </components.MenuList>
  );

  return (
    <Select
      options={options}
      value={selectedValue}
      onChange={handleChange}
      styles={customStyles}
      isSearchable={false}
      components={{
        DropdownIndicator,
        Control: CustomControl,
        ...(footer ? { MenuList: CustomMenuList } : {}),
      }}
    />
  );
};
